import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount)
}

// Headline rental rate for a vehicle. We PREFER Per KM: a vehicle that rents
// per-km leads with /km, not /day (pricePerDay is required on every car so it
// can't tell them apart). Priority: km -> outstation -> day -> hour, favouring
// a rate whose service type the owner enabled. Returns { amount, unit, suffix }
// or null when no positive rate exists.
export function headlineRate(car) {
  if (!car) return null
  const rates = {
    'km-based': car.pricePerKm > 0 && { amount: car.pricePerKm, unit: 'per km', suffix: '/km' },
    outstation: car.outstationPricePerKm > 0 && { amount: car.outstationPricePerKm, unit: 'per km', suffix: '/km' },
    daily: car.pricePerDay > 0 && { amount: car.pricePerDay, unit: 'per day', suffix: '/day' },
    local: car.pricePerHour > 0 && { amount: car.pricePerHour, unit: 'per hour', suffix: '/hr' },
  }
  const typeToKey = { daily: 'daily', rental: 'daily', 'km-based': 'km-based', outstation: 'outstation', local: 'local' }
  const enabled = new Set((Array.isArray(car.serviceTypes) ? car.serviceTypes : []).map((t) => typeToKey[t]).filter(Boolean))
  const priority = ['km-based', 'outstation', 'daily', 'local']
  const key = priority.find((k) => enabled.has(k) && rates[k]) || priority.find((k) => rates[k])
  return key ? rates[key] : null
}

// Formatted headline rate string, e.g. "₹15/km" or "₹3,000/day".
export function formatHeadlineRate(car) {
  const r = headlineRate(car)
  return r ? `${formatCurrency(r.amount)}${r.suffix}` : ''
}

export function formatDate(date) {
  return new Date(date).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export function formatDateTime(date) {
  return new Date(date).toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Relative "time ago" label, e.g. "just now", "5 min ago", "3 hours ago",
// "2 days ago". Returns "Never" when there's no date.
export function timeAgo(date) {
  if (!date) return 'Never';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Never';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr > 1 ? 's' : ''} ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

// Format name to Title Case (First Letter Uppercase)
export function formatName(name) {
  if (!name) return '';
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Format vehicle display name: "Toyota Innova - 2026 (KA01AB1234)"
export function formatVehicleName(car, options = {}) {
  if (!car) return '';
  const { showYear = true, showReg = false } = options;
  const make = formatName(car.make);
  const model = formatName(car.model);
  let name = `${make} ${model}`.trim();
  if (showYear && car.year) name += ` - ${car.year}`;
  if (showReg && car.registrationNumber) name += ` (${car.registrationNumber})`;
  return name;
}

// Format role to display format
export function formatRole(role) {
  if (!role) return '';
  // Handle special cases
  const roleMap = {
    'owner': 'Owner',
    'admin': 'Admin',
    'manager': 'Manager',
    'accountant': 'Accountant',
    'dispatcher': 'Dispatcher',
    'driver': 'Driver',
    'staff': 'Staff',
    'customer': 'Customer'
  };
  return roleMap[role.toLowerCase()] || role.charAt(0).toUpperCase() + role.slice(1);
}

// Amount in Indian-numbering words, e.g. 8080 -> "Eight Thousand Eighty Rupees Only"
export function amountInWords(num) {
  if (!num || isNaN(num) || num <= 0) return '';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const w = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + w(n % 100) : '');
    if (n < 100000) return w(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + w(n % 1000) : '');
    if (n < 10000000) return w(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + w(n % 100000) : '');
    return w(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + w(n % 10000000) : '');
  };
  return w(Math.floor(num)) + ' Rupees Only';
}

// Optimized Cloudinary image URL: resizes + compresses + serves modern format
// (WebP/AVIF). A 3MB original becomes ~30-80KB at card size. Non-Cloudinary
// URLs (or already-transformed ones) are returned unchanged.
export function cldImg(url, { w = 400, h, q = 'auto' } = {}) {
  if (!url || typeof url !== 'string' || !url.includes('/upload/')) return url;
  if (url.includes('/upload/f_auto') || url.includes('/upload/q_')) return url; // already optimized
  const t = `f_auto,q_${q},c_fill,w_${w}${h ? `,h_${h}` : ''}`;
  return url.replace('/upload/', `/upload/${t}/`);
}

/**
 * Downscale + compress an image File in the browser BEFORE uploading.
 * Turns multi-MB phone photos into ~200-400KB JPEGs so uploads are fast.
 * Best-effort: if the file can't be decoded (e.g. HEIC in Chrome), isn't an
 * image, or wouldn't get smaller, the original File is returned unchanged.
 */
export async function compressImage(file, { maxDim = 1600, quality = 0.8 } = {}) {
  try {
    if (!file || !file.type || !file.type.startsWith('image/')) return file;
    // HEIC/HEIF can't be drawn to a canvas in most browsers — upload as-is.
    if (/heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name || '')) return file;

    const bitmap = await createImageBitmap(file).catch(() => null);
    if (!bitmap) return file;

    let { width, height } = bitmap;
    // Already small enough — skip recompression.
    if (width <= maxDim && height <= maxDim && file.size < 1_000_000) {
      bitmap.close && bitmap.close();
      return file;
    }
    const scale = Math.min(1, maxDim / Math.max(width, height));
    width = Math.round(width * scale);
    height = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
    bitmap.close && bitmap.close();

    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file; // no gain — keep original

    const newName = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}
